import React, {useEffect, useRef, useState} from "react";
import {Box, Button, ButtonSize, ButtonType, Checkbox, Dropdown, DropdownMode, IDropdownItem, Input, Text} from "@renta-apps/renta-react-components";
import styles from "./CustomerInformation.module.scss";
import Localizer from "@/localization/Localizer";
import {phoneNumberPattern} from "@/helpers/Validators";
import {InvitedUser} from "@/models/server/InviteUserPostRequest";
import UserInvitationModel from "@/models/server/UserInvitationModel";
import {searchUsersAsync} from "@/services/UserService";

type CustomerInformationProps = {
    invitedUsers: InvitedUser[];
    onAddUser: (user: InvitedUser) => void;
}

const CustomerInformation: React.FC<CustomerInformationProps> = ({invitedUsers, onAddUser}) => {

    // Form and validation.
    const formRef = useRef<HTMLFormElement>(null);
    const [hasFormErrors, setHasFormErrors] = useState(false);
    const [isEmailInUse, setIsEmailInUse] = useState(false);
    const [isAddUserButtonDisabled, setIsAddUserButtonDisabled] = useState(false);

    // The free text user fields.
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [email, setEmail] = useState<string | null>(null);
    const [phone, setPhone] = useState<string | null>(null);

    // The search term for the existing user dropdown.
    const [searchTerm, setSearchTerm] = useState('');
    const [existingUsers, setExistingUsers] = useState<UserInvitationModel[]>([]);
    const [existingUsersOptions, setExistingUsersOptions] = useState<IDropdownItem[]>([]);
    const [selectedExistingUser, setSelectedExistingUser] = useState<IDropdownItem | undefined>(undefined);

    // The default selection for the radio selection should be none selected.
    const [useFreeTextUser, setUseFreeTextUser] = useState<boolean | null>(null);

    useEffect(() => {
        const searchUsers = async (search: string) => {
            const users = await searchUsersAsync(search.trim());
            setExistingUsers(users);
            setExistingUsersOptions(users.map(mapUserToDropdownItem));
        };

        if (searchTerm.length === 0 && selectedExistingUser) {
            return;
        }
        if (searchTerm.length < 3) {
            setExistingUsers([]);
            setExistingUsersOptions([]);
            return;
        }

        searchUsers(searchTerm).catch();
    }, [searchTerm, selectedExistingUser]);

    useEffect(() => {
        setIsAddUserButtonDisabled(shouldDisableAddUserButton());
    }, [useFreeTextUser, selectedExistingUser, firstName, lastName, email, phone]);

    const clearValues = () => {
        setExistingUsers([]);
        setExistingUsersOptions([]);
        setSelectedExistingUser(undefined);

        setEmail(null);
        setPhone(null);
        setFirstName("");
        setLastName("");

        setHasFormErrors(false);
        setIsEmailInUse(false);
    }

    const handleAddUserButtonClick = () => {
        let newUser: InvitedUser | null = null;

        if (useFreeTextUser === true) {
            // Validate if the user's e-mail already exists in the Invitation List.
            if (invitedUsers.some(user => user.email === email)) {
                setHasFormErrors(true);
                setIsEmailInUse(true);
                return;
            }

            newUser = {
                firstName,
                lastName,
                email: email!.trim(),
                telephone: phone!.trim(),
            };
        } else if (useFreeTextUser === false) {
            // Validate existing user dropdown.
            if (!selectedExistingUser) return;

            const selectedUser = existingUsers.find(
                (user) => user.userId === selectedExistingUser.value
            );
            if (!selectedUser) return;

            const emailInUse = invitedUsers.some(user => user.email === selectedUser.email);
            if (emailInUse) {
                setHasFormErrors(true);
                setIsEmailInUse(true);
                return;
            }

            newUser = {
                firstName: selectedUser.firstName || "",
                lastName: selectedUser.lastName || "",
                email: selectedUser.email || "",
                telephone: selectedUser.phoneNumber || "",
            };
        }

        // Add user if validation passed.
        if (newUser) {
            onAddUser(newUser);
            clearValues();
            setUseFreeTextUser(null);
        }
    };

    const handleEmailChange = (value: string) => {
        setEmail(value)
        setIsEmailInUse(false);
    }

    const handleSelectedUserChange = (value: IDropdownItem) => {
        setSelectedExistingUser(value);
        setHasFormErrors(false);
        setIsEmailInUse(false);
    }

    const handleUserTypeChange = (useFreeTextUser: boolean) => {
        clearValues();
        setUseFreeTextUser(useFreeTextUser);
    };

    const mapUserToDropdownItem = (user: UserInvitationModel): IDropdownItem => {
        return user.firstName && user.lastName ? {
            name: `${user.firstName} ${user.lastName}`,
            value: user.userId,
            description: user.email,
        } : {
            name: user.email!,
            value: user.userId,
        };
    };

    const shouldDisableAddUserButton = () => {
        if (useFreeTextUser === null) {
            return true;
        }

        if (useFreeTextUser === false) {
            return !selectedExistingUser;
        }

        if (useFreeTextUser === true) {
            if (!firstName || !lastName || !email || !phone) {
                return true;
            }
            if (!formRef.current?.checkValidity()) {
                setHasFormErrors(true);
                return true;
            }
        }

        return false;
    };

    return (
        <form ref={formRef}
              className={`${styles.customerInformationContainer}
                          ${useFreeTextUser === true ? styles.freeTextUser : useFreeTextUser === false ? styles.existingUser : ''}
                          ${hasFormErrors ? styles.hasErrors : ''}`}
              data-cy="user-invitation-modal-customer-information"
        >
            <div className={styles.radioButtons} data-cy="user-invitation-modal-radio-buttons">
                <Checkbox checked={useFreeTextUser === false}
                          onChange={() => handleUserTypeChange(false)}
                          label={Localizer.userInvitationModalUserExistingEasyUser}
                          className={styles.radioCheckbox}
                />
                <Checkbox checked={useFreeTextUser === true}
                          onChange={() => handleUserTypeChange(true)}
                          label={Localizer.userInvitationModalUserNewEasyUser}
                          className={styles.radioCheckbox}
                />
            </div>

            {useFreeTextUser === null ? null : (
                // Both the dropdown and text input fields should be hidden when no selection is made.
                useFreeTextUser === true ? (
                    <>
                        <Input type="text"
                                id={"customer_info_free_text_first_name"}
                                label={Localizer.genericFirstName}
                                value={firstName}
                                className={`${styles.freeTextFirstName} ${styles.inputField}`}
                                onChange={(value: string) => setFirstName(value)}
                                inputProperties={{
                                    required: true,
                                    name: "firstName",
                                }}
                        />
                        <Input type="text"
                                id={"customer_info_free_text_last_name"}
                                label={Localizer.genericLastName}
                                value={lastName}
                                className={`${styles.freeTextLastName} ${styles.inputField}`}
                                onChange={(value: string) => setLastName(value)}
                                inputProperties={{
                                    required: true,
                                    name: "lastName",
                                }}
                        />
                        <Box display="flex" flexDirection="column" gap={4} className={`${styles.freeTextEmail}`}>
                            <Input type="email"
                                    id={"customer_info_free_text_email"}
                                    label={Localizer.genericEmail}
                                    value={email}
                                    onChange={(value: string) => handleEmailChange(value)}
                                    className={`${styles.inputField} ${isEmailInUse ? styles.error : ''}`}
                                    inputProperties={{
                                        required: true,
                                        name: "contactUserEmail",
                                        maxLength: 200,
                                    }}
                            />
                            {isEmailInUse && (
                                <Text tag="span" size="Small" className={`${styles.information} ${styles.error}`} data-cy="customer-info-free-text-email-error">
                                    {Localizer.userInvitationModalUserUserAlreadyInList}
                                </Text>
                            )}
                        </Box>

                        <Input type="tel"
                                id={"customer_info_free_text_phone"}
                                label={Localizer.genericPhoneNumber}
                                value={phone}
                                className={`${styles.freeTextPhone} ${styles.inputField}`}
                                onChange={(value: string) => setPhone(value)}
                                inputProperties={{
                                    required: true,
                                    pattern: phoneNumberPattern(),
                                    name: "contactUserPhone",
                                    maxLength: 20,
                                }}
                        />
                    </>
                ) : (
                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={4} className={`${styles.existingUser}`}>
                        <Dropdown id="select-user-dropdown"
                                  className={`${styles.customerInformationDropdown} ${isEmailInUse ? styles.error : ''}`}
                                  items={existingUsersOptions}
                                  label={Localizer.inviteUserSelectUser}
                                  selectedItem={selectedExistingUser ?? []}
                                  onChange={(value: IDropdownItem) => handleSelectedUserChange(value)}
                                  onSearch={(value: string) => setSearchTerm(value)}
                                  allowEmpty
                                  searchable
                                  mode={DropdownMode.DROPDOWN}
                        />
                        <Text tag="span" size="Small" className={styles.information} data-cy="select-user-description">
                            {Localizer.userInvitationModalUserSelectUserDescription}
                        </Text>
                        {isEmailInUse && (
                            <Text tag="span" size="Small" className={`${styles.information} ${styles.error}`} data-cy="select-user-error">
                                {Localizer.userInvitationModalUserUserAlreadyInList}
                            </Text>
                        )}
                    </Box>
                )
            )}

            <Box display="flex" flexDirection="column" alignItems="center" className={styles.addUserButton}>
                <Button onClick={() => handleAddUserButtonClick()}
                        size={ButtonSize.Default}
                        type={ButtonType.Primary}
                        id="add-user-to-list-button"
                        disabled={isAddUserButtonDisabled}
                >
                    {Localizer.userInvitationModalUserAddUser}
                </Button>
            </Box>


        </form>
    );
};

export default CustomerInformation;
